/**
 * Error Tracking Utility
 * 
 * Optional error tracking service integration (Sentry). This module provides
 * a lightweight wrapper around error tracking services that can be configured
 * via environment variables. If no tracking service is configured, all methods
 * are no-ops, making this safe to use in all environments.
 * 
 * To enable Sentry:
 * 1. Install: npm install @sentry/react
 * 2. Set VITE_SENTRY_DSN in your .env file
 * 3. Optionally set VITE_SENTRY_ENVIRONMENT (defaults to production/development)
 * 
 * Usage:
 *   import { initErrorTracking, captureException, captureMessage } from '@/shared/utils/errorTracking';
 *   
 *   // Initialize at app startup (in main.jsx)
 *   initErrorTracking();
 *   
 *   // Capture errors
 *   captureException(error, { context });
 *   captureMessage('Something happened', 'warning');
 */

const isDev = import.meta.env?.DEV ?? import.meta.env?.MODE === 'development';
const sentryDsn = import.meta.env?.VITE_SENTRY_DSN;
const sentryEnvironment = import.meta.env?.VITE_SENTRY_ENVIRONMENT || 
  (isDev ? 'development' : 'production');

let isInitialized = false;
let Sentry = null;

/**
 * Initialize error tracking service (Sentry) if configured
 */
export const initErrorTracking = () => {
  if (isInitialized) {
    return;
  }

  // Only initialize if DSN is provided
  if (!sentryDsn) {
    return;
  }

  // Dynamically import Sentry to avoid bundling it if not configured
  import('@sentry/react')
    .then((sentryModule) => {
      // Handle both default and named exports
      Sentry = sentryModule.default || sentryModule;
      
      // Check if Sentry is properly loaded
      if (!Sentry || typeof Sentry.init !== 'function') {
        if (isDev) {
          // Use console here as logger might not be initialized yet during error tracking setup
          // eslint-disable-next-line no-console
          console.warn('[Error Tracking] Sentry module loaded but init method not found.');
        }
        return;
      }
      
      const integrations = [];
      
      // Add browser tracing integration if available (Sentry v7+)
      if (Sentry.browserTracingIntegration) {
        integrations.push(Sentry.browserTracingIntegration());
      }
      
      // Add replay integration if available (Sentry v7+)
      if (Sentry.replayIntegration) {
        integrations.push(Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }));
      }
      
      Sentry.init({
        dsn: sentryDsn,
        environment: sentryEnvironment,
        
        // Performance monitoring
        tracesSampleRate: isDev ? 1.0 : 0.1, // 100% in dev, 10% in prod
        
        // Session replay (optional - can be enabled for better debugging)
        replaysSessionSampleRate: isDev ? 1.0 : 0.1,
        replaysOnErrorSampleRate: 1.0, // Always capture on error
        
        // Filter sensitive data
        beforeSend(event, hint) {
          // Don't send events in development (unless explicitly testing)
          if (isDev && import.meta.env?.VITE_SENTRY_FORCE_SEND !== 'true') {
            return null;
          }
          
          // Filter out sensitive information
          if (event.request) {
            // Remove auth headers
            if (event.request.headers) {
              delete event.request.headers.Authorization;
              delete event.request.headers['x-user-id'];
              delete event.request.headers['service-id'];
            }
            
            // Don't send full URLs (may contain tokens)
            if (event.request.url) {
              try {
                const url = new URL(event.request.url);
                // Remove query params that might contain tokens
                url.search = '';
                event.request.url = url.toString();
              } catch (e) {
                // Invalid URL, remove it
                delete event.request.url;
              }
            }
          }
          
          // Filter user data
          if (event.user) {
            // Keep user ID for tracking, but remove other PII
            const userId = event.user.id;
            event.user = userId ? { id: userId } : null;
          }
          
          return event;
        },
        
        // Integrations
        integrations,
      });
      
      isInitialized = true;
      
      if (isDev) {
        // Use console here as logger might not be initialized yet during error tracking setup
        // eslint-disable-next-line no-console
        console.log('[Error Tracking] Sentry initialized successfully.');
      }
    })
    .catch((error) => {
      // Fail silently if Sentry can't be loaded (e.g., package not installed)
      if (isDev) {
        // Use console here as logger might not be initialized yet during error tracking setup
        // eslint-disable-next-line no-console
        console.warn('[Error Tracking] Sentry not available. Install @sentry/react to enable error tracking:', error.message);
      }
    });
};

/**
 * Capture an exception/error
 * @param {Error|string} error - Error object or error message
 * @param {Object} context - Additional context (tags, extra data, level)
 */
export const captureException = (error, context = {}) => {
  if (!Sentry) {
    return;
  }

  try {
    const { level = 'error', tags, extra, fingerprint } = context;
    
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      level,
      tags,
      extra,
      fingerprint,
    });
  } catch (err) {
    // Fail silently if capture fails
    if (isDev) {
      // Use console here as logger might not be initialized yet during error tracking setup
      // eslint-disable-next-line no-console
      console.warn('[Error Tracking] Failed to capture exception:', err);
    }
  }
};

/**
 * Capture a message (non-error event)
 * @param {string} message - Message to capture
 * @param {string} level - Severity level (debug, info, warning, error)
 * @param {Object} context - Additional context
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  if (!Sentry) {
    return;
  }

  try {
    const { tags, extra, fingerprint } = context;
    
    Sentry.captureMessage(message, {
      level,
      tags,
      extra,
      fingerprint,
    });
  } catch (err) {
    // Fail silently if capture fails
    if (isDev) {
      // Use console here as logger might not be initialized yet during error tracking setup
      // eslint-disable-next-line no-console
      console.warn('[Error Tracking] Failed to capture message:', err);
    }
  }
};

/**
 * Set user context for error tracking
 * @param {Object} user - User information (id, email, etc.)
 */
export const setUser = (user) => {
  if (!Sentry) {
    return;
  }

  try {
    Sentry.setUser(user ? {
      id: user.id,
      // Only include non-sensitive user data
      email: user.email ? undefined : undefined, // Don't include email by default
    } : null);
  } catch (err) {
    // Fail silently
    if (isDev) {
      // Use console here as logger might not be initialized yet during error tracking setup
      // eslint-disable-next-line no-console
      console.warn('[Error Tracking] Failed to set user:', err);
    }
  }
};

/**
 * Clear user context (e.g., on logout)
 */
export const clearUser = () => {
  setUser(null);
};

/**
 * Add breadcrumb for debugging
 * @param {Object} breadcrumb - Breadcrumb data
 */
export const addBreadcrumb = (breadcrumb) => {
  if (!Sentry) {
    return;
  }

  try {
    Sentry.addBreadcrumb(breadcrumb);
  } catch (err) {
    // Fail silently
    if (isDev) {
      // Use console here as logger might not be initialized yet during error tracking setup
      // eslint-disable-next-line no-console
      console.warn('[Error Tracking] Failed to add breadcrumb:', err);
    }
  }
};

/**
 * Check if error tracking is initialized and available
 */
export const isErrorTrackingEnabled = () => {
  return isInitialized && Sentry !== null;
};

